/* eslint-disable @typescript-eslint/no-explicit-any */
import type { FC } from 'react'
import React from 'react'

import CardCard from './card-card'
import CardContent from './card-content'
import CardFooter from './card-footer'
import CardHeader from './card-header'
import CardTitle from './card-title'
import type {
  CardComponents,
  CardHeaderProps,
  CardProps,
  CardTitleProps
} from './types'
import { EMPTY_OBJECT } from '../../utils'
import { useUniqueId } from '../../utils/unique-id'
import Flex from '../_primitives/flex'

// @ts-ignore
const Card: FC<CardProps> & CardComponents = React.forwardRef<
  HTMLDivElement,
  CardProps
>(function Card(props, ref) {
  const {
    a11yDescriptionId: _a11yDescriptionId,
    a11yTitleId: _a11yTitleId,
    variant,
    children,
    footer,
    headerActions,
    footerProps,
    title,
    headerProps,
    contentProps,
    ...rest
  } = props
  const { titleProps, ...restHeaderProps } =
    headerProps || (EMPTY_OBJECT as CardHeaderProps)
  const titleId = useUniqueId('card-title')
  const descriptionId = useUniqueId('card-description')
  const a11yDescriptionId = _a11yDescriptionId || descriptionId
  const a11yTitleId = _a11yTitleId || titleId

  function wrapHeaderAction(children: React.ReactNode) {
    return title ? (
      <Flex
        className="csg-tui-card-header-action-container"
        justifyContent="flex-end"
        alignSelf="start"
        gap="xs"
      >
        {children}
      </Flex>
    ) : (
      children
    )
  }

  function wrapCardContent(children: React.ReactNode) {
    return !!title || !!headerActions || !!footer ? (
      <CardContent id={a11yDescriptionId} {...contentProps}>
        {children}
      </CardContent>
    ) : (
      children
    )
  }

  return (
    <CardCard
      a11yDescriptionId={title || footer ? a11yDescriptionId : undefined}
      a11yTitleId={title ? a11yTitleId : undefined}
      ref={
        ref
          ? ref
          : !title && !headerActions && !footer
            ? contentProps?.ref
            : undefined
      }
      useContext={!!variant}
      variant={variant}
      {...rest}
    >
      {(title || headerActions) && (
        <CardHeader
          {...(title
            ? headerActions
              ? {
                  display: 'grid',
                  gap: 'xxs',
                  gridTemplateColumns: 'minmax(20px, auto) 1fr',
                  ...restHeaderProps
                }
              : {
                  justifyContent: 'flex-start',
                  ...restHeaderProps
                }
            : {
                justifyContent: 'flex-end',
                ...restHeaderProps
              })}
        >
          {typeof title === 'string' ? (
            <CardTitle id={a11yTitleId} {...titleProps}>
              {title}
            </CardTitle>
          ) : typeof title === 'function' ? (
            title({ id: a11yTitleId, ...titleProps } as CardTitleProps)
          ) : (
            title
          )}
          {headerActions ? wrapHeaderAction(headerActions) : null}
        </CardHeader>
      )}
      {wrapCardContent(children)}
      {footer && <CardFooter {...footerProps}>{footer}</CardFooter>}
    </CardCard>
  )
})

Card.Card = CardCard
Card.Header = CardHeader
Card.Content = CardContent
Card.Footer = CardFooter
Card.Title = CardTitle

export default Card
